import React, { useState, useEffect, useContext } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import AuthContext from "../../contexts/AuthContext/AuthContext";
import "./OrderPayment.css";

const OrderPayment = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isSignedIn } = useContext(AuthContext);
  const [order, setOrder] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);

  const SERVER_URL = process.env.REACT_APP_SERVER_URL;

  useEffect(() => {
    console.log("OrderPayment - isSignedIn:", isSignedIn);
    console.log("OrderPayment - Token:", localStorage.getItem("access_token"));

    const fetchOrder = async () => {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setMessage("Please sign in to continue.");
        navigate("/signin");
        return;
      }

      try {
        const response = await axios.get(
          `${SERVER_URL}/api/orders/${orderId}/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        console.log("OrderPayment - Fetched order:", response.data);
        setOrder(response.data);
        setLoading(false);

        const params = new URLSearchParams(location.search);
        if (params.get("success")) {
          setMessage("Payment successful! Order status updated.");
        } else if (params.get("canceled")) {
          setMessage("Payment was canceled. Order status unchanged.");
        }
      } catch (error) {
        console.error("Error fetching order:", error.response?.data);
        if (error.response?.status === 401) {
          setMessage("Session expired. Please sign in again.");
          navigate("/signin");
        } else {
          setMessage("Failed to load order details.");
        }
        setLoading(false);
      }
    };

    fetchOrder();
  }, [navigate, orderId, location.search, SERVER_URL, isSignedIn]);

  const handlePay = async () => {
    try {
      const response = await axios.post(
        `${SERVER_URL}/api/orders/${orderId}/pay/`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );
      window.location.href = response.data.payment_link;
    } catch (error) {
      console.error("Error generating payment link:", error.response?.data);
      setMessage(error.response?.data.message || "Failed to initiate payment.");
    }
  };

  const handleCancel = async () => {
    try {
      const response = await axios.post(
        `${SERVER_URL}/api/orders/${orderId}/cancel/`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );
      setMessage(response.data.message);
      const updatedOrder = await axios.get(
        `${SERVER_URL}/api/orders/${orderId}/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );
      setOrder(updatedOrder.data);
    } catch (error) {
      console.error("Error canceling order:", error.response?.data);
      setMessage(error.response?.data.message || "Cancellation failed.");
    }
  };

  if (loading) {
    return (
      <div className="order-payment-loading" role="status" aria-live="polite">
        Loading...
      </div>
    );
  }

  if (!order) {
    return (
      <div className="order-payment-error" role="alert" aria-live="assertive">
        <p>{message}</p>
      </div>
    );
  }

  const amountDue =
    order.status === "Approved"
      ? order.video.first_installment
      : order.status === "Translation Completed"
      ? order.video.second_installment
      : 0;

  return (
    <div className="order-payment-container">
      <Helmet>
        <title>Order Payment – LingoLab</title>
        <meta
          name="description"
          content="Pay for your LingoLab video translation order securely online. View order details, status, and complete your payment."
        />
        <meta name="robots" content="noindex, follow" />
        <link
          rel="canonical"
          href={`https://lingolab.ca/order-payment/${orderId}`}
        />
        <meta property="og:title" content="Order Payment – LingoLab" />
        <meta
          property="og:description"
          content="Pay for your LingoLab video translation order securely online. View order details, status, and complete your payment."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content={`https://lingolab.ca/order-payment/${orderId}`}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@LingLabAI" />
        <meta name="twitter:title" content="Order Payment – LingoLab" />
        <meta
          name="twitter:description"
          content="Pay for your LingoLab video translation order securely online. View order details, status, and complete your payment."
        />
        <meta name="twitter:image" content="https://lingolab.ca/ll-X.png" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "LingoLab",
              "url": "https://lingolab.ca/",
              "logo": "https://lingolab.ca/ll-X.png",
              "sameAs": [
                "https://twitter.com/LingLabAI"
              ],
              "description": "LingoLab uses advanced AI to translate and localize your videos into 80+ languages, preserving the original voice and syncing lips for a native look.",
              "contactPoint": {
                "@type": "ContactPoint",
                "email": "support@lingolab.ca",
                "contactType": "customer support",
                "url": "https://lingolab.ca/contact"
              }
            }
          `}
        </script>
      </Helmet>
      <div
        className="order-payment-card"
        role="region"
        aria-labelledby="order-payment-title"
      >
        <h2 className="order-payment-title" id="order-payment-title">
          Order Payment
        </h2>

        {/* Order Details */}
        <div className="order-payment-details">
          <div className="order-payment-field">
            <span className="order-payment-label">Order ID:</span>
            <span>{order.id}</span>
          </div>
          <div className="order-payment-field">
            <span className="order-payment-label">Translation:</span>
            <span>
              {order.video.from_language.name} to {order.video.to_language.name}
            </span>
          </div>
          <div className="order-payment-field">
            <span className="order-payment-label">Video URL:</span>
            <span className="order-payment-url">
              <a
                href={order.video.original_video_url}
                target="_blank"
                rel="noopener noreferrer"
                className="order-payment-link"
                aria-label="View original video"
                onTouchStart={() => {}}
              >
                View Video
              </a>
            </span>
          </div>
          <div className="order-payment-field">
            <span className="order-payment-label">Status:</span>
            <span>{order.status}</span>
          </div>
          <div className="order-payment-field">
            <span className="order-payment-label">Total Amount:</span>
            <span>${order.video.price}</span>
          </div>
          <div className="order-payment-field">
            <span className="order-payment-label">Amount Due:</span>
            <span>${amountDue}</span>
          </div>
        </div>

        {/* Buttons */}
        <div className="order-payment-buttons">
          <button
            onClick={handlePay}
            onTouchStart={() => {}}
            className="order-payment-pay-button"
            disabled={
              order.status !== "Approved" &&
              order.status !== "Translation Completed"
            }
            aria-label="Pay now"
          >
            Pay Now
          </button>
          <button
            onClick={handleCancel}
            onTouchStart={() => {}}
            className="order-payment-cancel-button"
            disabled={
              order.status !== "Submitted" && order.status !== "Approved"
            }
            aria-label="Cancel order"
          >
            Cancel Order
          </button>
        </div>

        {/* Message */}
        {message && (
          <div className="order-payment-message">
            <p>{message}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderPayment;
